<script setup lang="ts">
    import { AtmFormSelectOptionsOutOfStock } from '#components';

    const LEGGIE_KICKSTAND_HANDLES = ['leggie-kickstand', 'kickstand'];

    export interface OptionSelectProps {
        option: IProductOption;
        config?: IConfigurable;
        teleport?: boolean;
        isPDP?: boolean;
    }
    const props = defineProps<OptionSelectProps>();

    const { t, translateSafe } = useLocales();
    const route = useRoute();
    const router = useRouter();
    const leggieSelectedOption = ref('');

    const handle = computed(() => props.config?.raw?.handle ?? '');

    // Leggie values
    // handle: TODO: Assign a value here once a matching handle is found
    // option type: ['leggie-kickstand', 'LEGGIE Kickstand (Size)']
    const isLeggieKickstand = computed(
        () =>
            LEGGIE_KICKSTAND_HANDLES.includes(handle.value) ||
            LEGGIE_KICKSTAND_HANDLES.some((handle) => props.option.type?.toLowerCase().includes(handle)),
    );
    const isCrankset = computed(() => ['woom-off-off-air-kurbel-gen-b', 'crank-set'].includes(handle.value)); // San Diego recall on crankset
    const isKidsHelmet = computed(() => ['helmet', 'kids-helmet'].includes(handle.value));

    const selectedValue = computed({
        get() {
            if (isLeggieKickstand.value) {
                return leggieSelectedOption.value;
            }
            return props.option.selectedRef.value;
        },
        async set(value) {
            if (!value) return;

            if (!isLeggieKickstand.value) {
                return props.option.setSelected(value as string);
            }

            leggieSelectedOption.value = value;
            const [newValue, model] = (value as string).split(':');
            props.option.setSelected(newValue);
            // Need to wait for router to update
            await wait(100);
            if (props.isPDP) router.replace({ query: { ...route.query, model } });
            props.config?.setCustomField('leggie-model', model);
        },
    });

    const optionValues = computed(() => {
        if (!isLeggieKickstand.value) return props.option.values;

        return leggieKickstandOptionValues.map(({ label, value }) => {
            const { disabled } = props.option.values.find((optionValue) => value.startsWith(optionValue.value.toLowerCase())) ?? {};
            return {
                label,
                value,
                disabled,
            };
        });
    });

    // San Diego crank set recall - display a different label, value should remain the same
    const translateCrank = (label: string) => {
        if (label === 'OFF 5') return 'Size 5';
        if (['OFF 6', 'woom 6'].includes(label)) return 'Size 6';
        return label;
    };

    const productOptionType = computed(() => {
        if (isKidsHelmet.value) return 'sizes.helmet';
        return props.option.type.toLowerCase();
    });

    const translate = (label: string): string => {
        if (!label) return label;
        if (isCrankset.value) return translateCrank(label);

        return translateSafe(`product.${productOptionType.value}.${label}`, label);
    };
    const translateLabel = (label: string): string => {
        return translateSafe(`product.option.${label.toLowerCase()}`, label);
    };

    onMounted(() => {
        if (!isLeggieKickstand.value) return;

        let modelSearch = `${unArray(route.query.variant)?.toLowerCase() ?? ''}`;

        if (route.query.model) modelSearch += `:${unArray(route.query.model)}`;

        const firstVariant = leggieKickstandOptionValues.find((option) => option.value.startsWith(modelSearch)) || leggieKickstandOptionValues[0];
        leggieSelectedOption.value = firstVariant.value;
    });
</script>
<template>
    <mol-form-select
        class="mol-product-option-select"
        v-model:dropdown-value="selectedValue"
        :label="isLeggieKickstand ? t('leggiekickstand.label') : translateLabel(option.type)"
        :options="optionValues"
        :teleport="teleport"
        :translate="translate"
        :postfixComponent="AtmFormSelectOptionsOutOfStock"
        :isCrankset="isCrankset" />
</template>
